import { FormEvent, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import "../../main.scss"
import "./User.scss"
import { authAPI } from "../api/authApi"
import { useAuth } from "../auth/AuthContext"

function RegisterComplete() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
  })
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [tokenChecked, setTokenChecked] = useState(false)
  const [tokenUnusable, setTokenUnusable] = useState(false)
  const [resendEmail, setResendEmail] = useState("")
  const [resendStatus, setResendStatus] = useState<string | null>(null)
  const [resendBusy, setResendBusy] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refreshUser } = useAuth()
  const returnPathParam = searchParams.get('returnPath')
  const isPublicFormFlow = Boolean(returnPathParam?.includes('/forms/'))
  const completionLabel = isPublicFormFlow ? 'Complete Submission' : 'Complete Registration'
  const completingLabel = isPublicFormFlow ? 'Completing submission...' : 'Completing...'

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token')
    if (!tokenFromUrl) {
      setError('Invalid registration link. No token found.')
      setToken(null)
      setTokenChecked(true)
      return
    }
    setToken(tokenFromUrl)
    authAPI.registerCheckToken(tokenFromUrl).then((result) => {
      if (!result.valid) {
        setTokenUnusable(true)
        setError(result.error ?? 'Link is no longer valid.')
      }
      setTokenChecked(true)
    }).catch(() => {
      setTokenUnusable(true)
      setError('Unable to verify link.')
      setTokenChecked(true)
    })
  }, [searchParams])

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    const returnPathParam = searchParams.get('returnPath')
    if (!tokenParam || !returnPathParam || returnPathParam.indexOf('draftId=') === -1) {
      return
    }
    const queryStart = returnPathParam.indexOf('?')
    if (queryStart === -1) return
    const query = returnPathParam.slice(queryStart + 1)
    const draftId = new URLSearchParams(query).get('draftId')
    if (!draftId) return
    authAPI.registerPrefill(tokenParam, draftId).then((prefill) => {
      if (prefill.firstName !== undefined || prefill.lastName !== undefined) {
        setFormData((prev) => ({
          ...prev,
          firstName: prefill.firstName ?? prev.firstName,
          lastName: prefill.lastName ?? prev.lastName
        }))
      }
    }).catch(() => { })
  }, [searchParams])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (busy) {
      return
    }

    setBusy(true)
    setError("")

    if (!token) {
      setError('Invalid registration link')
      setBusy(false)
      return
    }

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
    }

    if (!payload.firstName || !payload.lastName) {
      setError("First and last name are required")
      setBusy(false)
      return
    }

    try {
      await authAPI.registerComplete(token, payload)

      await refreshUser()

      if (returnPathParam && returnPathParam.startsWith('/')) {
        navigate(returnPathParam)
        return
      }
      navigate("/")
    } catch (err: any) {
      const msg = err?.message ?? ""
      const isTokenUnusable =
        /already been used|already used|expired|invalid token/i.test(msg)
      if (isTokenUnusable) {
        setTokenUnusable(true)
        setError(msg)
      } else if (err?.status === 400) {
        setError(err.message || "Token, firstName, and lastName are required")
      } else {
        setError(err.message || "Registration failed")
      }
    } finally {
      setBusy(false)
    }
  }

  if (!tokenChecked) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="panel w-[300px] p-6 shadow-md">
          <h1 className="text-center mb-4">Validating Link...</h1>
          <p className="text-sm text-center text-gray-500">Please wait while we confirm your registration token.</p>
        </div>
      </div>
    )
  }

  if (!token) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="panel w-[300px] p-6 shadow-md">
          <h1 className="text-center mb-4">Invalid Link</h1>
          <p className="text-sm text-red-500 text-center mb-4">{error || 'This registration link is invalid or has expired.'}</p>
          <div className="flex justify-center">
            <button className="buttonSubmit" onClick={() => navigate("/register")}>
              Request a new link
            </button>
          </div>
        </div>
      </div>
    )
  }

  const handleResendLink = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const normalized = resendEmail.trim().toLowerCase()
    if (!normalized || !normalized.includes("@")) {
      setResendStatus("Enter a valid email address.")
      return
    }
    setResendBusy(true)
    setResendStatus(null)
    try {
      if (isPublicFormFlow && returnPathParam) {
        await authAPI.publicFormAuthInitiate(normalized, {
          returnPath: returnPathParam,
        })
      } else {
        await authAPI.registerInitiate(normalized, {
          returnPath: returnPathParam && returnPathParam.startsWith("/") ? returnPathParam : undefined,
        })
      }
      setResendStatus("We sent you a new link. Check your email.")
    } catch (err: any) {
      setResendStatus(err?.message ?? "Unable to send link. Try again.")
    } finally {
      setResendBusy(false)
    }
  }

  if (tokenUnusable) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="panel w-[300px] p-6 shadow-md">
          <h1 className="text-center mb-4">Link Not Usable</h1>
          <p className="text-sm text-red-500 text-center mb-4">
            {error || "This registration link has already been used or is no longer valid."}
          </p>
          <p className="text-sm text-gray-600 text-center mb-4">
            Enter your email to receive a new link.
          </p>
          <form onSubmit={handleResendLink} className="space-y-3">
            <input
              type="email"
              placeholder="Email address"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              className="textInput w-full"
              disabled={resendBusy}
              autoComplete="email"
            />
            <button
              type="submit"
              className={"buttonSubmit w-full" + (resendBusy ? " opacity-50 cursor-progress" : "")}
              disabled={resendBusy}
            >
              {resendBusy ? "Sending..." : "Resend link"}
            </button>
            {resendStatus && (
              <p
                className={
                  "text-sm text-center " +
                  (resendStatus.startsWith("We sent") ? "text-green-600" : "text-red-500")
                }
              >
                {resendStatus}
              </p>
            )}
          </form>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "300px",
          padding: "20px",
          boxSizing: "border-box",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h1 className="py-5">{completionLabel}</h1>
        {error && <p style={{ color: "red" }}>{error}</p>}
        <input
          type="text"
          placeholder="First Name"
          value={formData.firstName}
          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
          className="textInput"
          required
          disabled={busy}
          autoComplete="given-name"
        />
        <input
          type="text"
          placeholder="Last Name"
          value={formData.lastName}
          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          className="textInput"
          required
          disabled={busy}
          autoComplete="family-name"
        />
        <p className="text-xs text-gray-500 mt-1">
          We'll finish setting up your account with the information above.
        </p>
        <button
          type="submit"
          className={
            "buttonSubmit mt-5" +
            (busy ? " opacity-50 cursor-progress" : "")
          }
          disabled={busy}
        >
          {busy ? completingLabel : completionLabel}
        </button>
        {!isPublicFormFlow && (
          <div className="w-full flex justify-end text-blue-400 mt-10">
            <button type="button" onClick={() => navigate("/login")}>
              Login
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

export default RegisterComplete


